import { Vector3 } from 'three';
import { TrackRuntime } from '../track/runtime';
import { ARROW_PROFILE, trackEnvelopeRadius } from '../geometry/profile';
import { worldMetersToPrintMm, printMmToWorldMetres } from './types';
import { JOINT_PIN_LENGTH_M, JOINT_SOCKET_PLUG_M } from './joints';

const SAMPLE_STEP_M = 0.5;

export interface ObbMm {
  x: number;
  y: number;
  z: number;
}

export interface ProposeTrackCutsOpts {
  /**
   * Arc-length positions of support↔track connections (world metres).
   * Cuts are kept at least {@link supportCutClearanceMm} away from these.
   */
  supportAttachS?: number[];
  /** Min arc distance from a support attach to a cut, in print mm. */
  supportCutClearanceMm?: number;
}

/** Max side of the print-mm OBB (local frame of the segment midpoint). */
export function obbMaxSideMm(obb: ObbMm): number {
  return Math.max(obb.x, obb.y, obb.z);
}

/**
 * Leave room for a click-fit pin/sleeve so a flush cube fit does not overflow
 * after joints are added.
 */
export function printChunkFitLimitMm(chunkSizeMm: number): number {
  const jointBudgetMm = worldMetersToPrintMm(JOINT_PIN_LENGTH_M + JOINT_SOCKET_PLUG_M);
  return Math.max(chunkSizeMm - jointBudgetMm, chunkSizeMm * 0.75);
}

/** Shortest track interval we will emit, in world metres. */
export function minTrackChunkArcM(chunkSizeMm: number): number {
  return Math.max(printMmToWorldMetres(chunkSizeMm) * 0.1, SAMPLE_STEP_M * 4);
}

/**
 * PCA-oriented bounding box of world-metre points, in print mm.
 * Longest side is the printable extent after rotating the part onto the cube.
 */
export function pointsObbMm(points: Vector3[]): ObbMm {
  if (points.length === 0) return { x: 0, y: 0, z: 0 };

  const c = new Vector3();
  for (const p of points) c.add(p);
  c.multiplyScalar(1 / points.length);

  let xx = 0;
  let xy = 0;
  let xz = 0;
  let yy = 0;
  let yz = 0;
  let zz = 0;
  for (const p of points) {
    const dx = p.x - c.x;
    const dy = p.y - c.y;
    const dz = p.z - c.z;
    xx += dx * dx;
    xy += dx * dy;
    xz += dx * dz;
    yy += dy * dy;
    yz += dy * dz;
    zz += dz * dz;
  }
  const inv = 1 / points.length;
  const axes = principalAxes(xx * inv, xy * inv, xz * inv, yy * inv, yz * inv, zz * inv);

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  const d = new Vector3();
  for (const p of points) {
    d.subVectors(p, c);
    const x = d.dot(axes[0]);
    const y = d.dot(axes[1]);
    const z = d.dot(axes[2]);
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  return {
    x: worldMetersToPrintMm(maxX - minX),
    y: worldMetersToPrintMm(maxY - minY),
    z: worldMetersToPrintMm(maxZ - minZ),
  };
}

/**
 * Oriented bounding box of the track envelope on [s0, s1], in print mm.
 * Axes are the banked frame at the segment midpoint.
 */
export function trackSegmentObbMm(runtime: TrackRuntime, s0: number, s1: number): ObbMm {
  const L = runtime.totalLength;
  const a = Math.max(0, Math.min(s0, s1));
  const b = Math.min(L, Math.max(s0, s1));
  if (b - a < 1e-9) return { x: 0, y: 0, z: 0 };

  const mid = runtime.frameAt((a + b) / 2);
  const half = trackEnvelopeRadius(ARROW_PROFILE);
  const offsets: [number, number][] = [
    [-half, -half],
    [half, -half],
    [-half, half],
    [half, half],
    [0, 0],
  ];

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  const tmp = new Vector3();
  const d = new Vector3();
  const n = Math.max(2, Math.ceil((b - a) / SAMPLE_STEP_M));
  for (let i = 0; i <= n; i++) {
    const s = a + ((b - a) * i) / n;
    const f = runtime.frameAt(s);
    for (const [ox, oy] of offsets) {
      tmp.copy(f.railPos).addScaledVector(f.right, ox).addScaledVector(f.up, oy);
      d.subVectors(tmp, mid.pos);
      const x = d.dot(mid.right);
      const y = d.dot(mid.up);
      const z = d.dot(mid.tan);
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }
  }

  return {
    x: worldMetersToPrintMm(maxX - minX),
    y: worldMetersToPrintMm(maxY - minY),
    z: worldMetersToPrintMm(maxZ - minZ),
  };
}

export function segmentFitsBuildVolume(
  runtime: TrackRuntime,
  s0: number,
  s1: number,
  chunkSizeMm: number,
): boolean {
  return obbMaxSideMm(trackSegmentObbMm(runtime, s0, s1)) <= chunkSizeMm + 1e-6;
}

/**
 * Propose cut arc lengths so each track interval's OBB fits the cubic build volume
 * after click-fit joints are added (see {@link printChunkFitLimitMm}).
 *
 * Walks the centerline contiguously from s=0 — grow until the track no longer
 * fits, then cut. Always considers s=totalLength (the sample step otherwise
 * leaves a sliver at the end). Never skips ahead or pulls in nearby
 * non-contiguous track that happens to lie in the same spatial box. Also cuts
 * before a U-turn that would re-enter the chunk's spatial neighbourhood
 * (parallel return run). Cuts are snapped at least
 * {@link ProposeTrackCutsOpts.supportCutClearanceMm} away from support
 * connections so stub + elbow (beams 1–2) stay wholly inside one chunk.
 * Intervals shorter than {@link minTrackChunkArcM} are merged or rebalanced.
 *
 * Returns the full boundary list including 0 and totalLength.
 */
export function proposeTrackCuts(
  runtime: TrackRuntime,
  chunkSizeMm: number,
  opts?: ProposeTrackCutsOpts,
): number[] {
  const L = runtime.totalLength;
  if (L < 1e-6) return [0, 0];

  const attaches = sortedUnique(
    (opts?.supportAttachS ?? []).filter((s) => Number.isFinite(s) && s > 0 && s < L),
  );
  const clearanceM = printMmToWorldMetres(Math.max(0, opts?.supportCutClearanceMm ?? 0));

  const fitMm = printChunkFitLimitMm(chunkSizeMm);
  const minArcM = minTrackChunkArcM(chunkSizeMm);
  const boundaries: number[] = [0];
  let start = 0;
  const step = Math.min(SAMPLE_STEP_M, L / 2);
  // World-metre arc budget ≈ cube diagonal (print mm → m) — stops a helix from
  // folding indefinitely into the same OBB while still walking contiguously.
  const maxArcM = printMmToWorldMetres(chunkSizeMm) * Math.sqrt(3);
  // Parallel return: only when spatial distance is a real shortcut vs arc travel
  // (straight runs have spatial ≈ arc and must not trip this — that was cutting
  // chunks at ~½ cube side).
  const reentryProximityM = printMmToWorldMetres(chunkSizeMm) * 0.4;
  const reentryMinArcM = printMmToWorldMetres(chunkSizeMm) * 0.5;

  while (start < L - 1e-9) {
    // Grow contiguously from `start` until the next sample no longer fits.
    let prev = Math.min(start + step, L);
    let end = prev;
    let stoppedEarly = false;
    for (let s = prev; s < L - 1e-9; s += step) {
      const withinArc = s - start <= maxArcM + 1e-9;
      const foldsBack = sampleReentersEarlier(
        runtime,
        start,
        prev,
        s,
        reentryProximityM,
        reentryMinArcM,
      );
      if (foldsBack) {
        // Stop before the parallel return — do not refine into the fold.
        end = prev > start + 1e-6 ? prev : s;
        stoppedEarly = true;
        break;
      }
      if (withinArc && segmentFitsBuildVolume(runtime, start, s, fitMm)) {
        prev = s;
        end = s;
        continue;
      }
      // Binary-search the last fitting cut between prev and s so we fill the cube.
      if (prev > start + 1e-6 && s > prev + 1e-6) {
        end = refineCutEnd(runtime, start, prev, Math.min(s, start + maxArcM), fitMm);
      } else {
        end = prev > start + 1e-6 ? prev : s;
      }
      stoppedEarly = true;
      break;
    }
    // The step loop never samples s=L, so a leftover of (L-start) % step would
    // otherwise become its own sliver chunk at the end of the track.
    if (!stoppedEarly && end < L - 1e-6) {
      const foldsAtL = sampleReentersEarlier(
        runtime,
        start,
        end,
        L,
        reentryProximityM,
        reentryMinArcM,
      );
      const withinArc = L - start <= maxArcM + 1e-9;
      if (!foldsAtL && withinArc && segmentFitsBuildVolume(runtime, start, L, fitMm)) {
        end = L;
      } else if (end > start + 1e-6) {
        end = refineCutEnd(runtime, start, end, Math.min(L, start + maxArcM), fitMm);
      }
    }
    if (end >= L - 1e-6) {
      break;
    }

    // Snap the cut away from support connections (keep clearance for beams 1–2).
    const cutAt = snapCutAwayFromSupports(
      runtime,
      start,
      end,
      L,
      fitMm,
      maxArcM,
      attaches,
      clearanceM,
    );

    if (cutAt <= start + 1e-6) {
      // Forced progress when a single step already exceeds the cube.
      const forced = Math.min(L, start + step);
      if (forced >= L - 1e-6) break;
      boundaries.push(forced);
      start = forced;
      continue;
    }

    boundaries.push(cutAt);
    start = cutAt;
  }

  if (boundaries[boundaries.length - 1] < L - 1e-9) boundaries.push(L);
  else boundaries[boundaries.length - 1] = L;

  mergeTinyIntervals(runtime, boundaries, L, fitMm, maxArcM, minArcM, attaches, clearanceM);
  return boundaries;
}

/**
 * True when `newS` lies spatially near an earlier sample after a long arc travel
 * — i.e. a U-turn / parallel return (spatial shortcut), not a straight run
 * where distance ≈ arc length.
 */
function sampleReentersEarlier(
  runtime: TrackRuntime,
  start: number,
  prevEnd: number,
  newS: number,
  proximityM: number,
  minArcGapM: number,
): boolean {
  if (newS - start < minArcGapM + 1e-9) return false;
  const newPos = runtime.frameAt(newS).pos;
  const lim2 = proximityM * proximityM;
  for (let s = start; s <= prevEnd + 1e-9; s += SAMPLE_STEP_M) {
    const arcGap = newS - s;
    if (arcGap < minArcGapM) continue;
    const d2 = runtime.frameAt(s).pos.distanceToSquared(newPos);
    if (d2 > lim2) continue;
    // Shortcut: traveled far along the rail but ended up much closer in space.
    const dist = Math.sqrt(d2);
    if (dist < arcGap * 0.5) return true;
  }
  return false;
}

/** Largest end in (lo, hi] such that [start, end] still fits the build cube. */
function refineCutEnd(
  runtime: TrackRuntime,
  start: number,
  lo: number,
  hi: number,
  chunkSizeMm: number,
): number {
  let best = lo;
  let left = lo;
  let right = hi;
  for (let i = 0; i < 12; i++) {
    const mid = (left + right) / 2;
    if (segmentFitsBuildVolume(runtime, start, mid, chunkSizeMm)) {
      best = mid;
      left = mid;
    } else {
      right = mid;
    }
  }
  return best;
}

/**
 * Move a candidate cut so it is at least `clearanceM` from every support
 * attach, while staying inside (start, L) and preferably still fitting the cube.
 * Prefers cutting before an attach (support stays in the next chunk) when the
 * earlier cut still yields a usable segment; otherwise cuts after the attach
 * so stub + elbow remain in the current chunk.
 */
function snapCutAwayFromSupports(
  runtime: TrackRuntime,
  start: number,
  candidate: number,
  L: number,
  chunkSizeMm: number,
  maxArcM: number,
  attaches: number[],
  clearanceM: number,
): number {
  if (clearanceM <= 1e-9 || attaches.length === 0) return candidate;

  let cut = candidate;
  // Iterate a few times — adjusting for one attach can land near another.
  for (let iter = 0; iter < 8; iter++) {
    let nearest: number | null = null;
    let nearestDist = Infinity;
    for (const a of attaches) {
      const d = Math.abs(cut - a);
      if (d < clearanceM - 1e-9 && d < nearestDist) {
        nearest = a;
        nearestDist = d;
      }
    }
    if (nearest == null) break;

    const before = nearest - clearanceM;
    const after = nearest + clearanceM;

    const beforeOk =
      before > start + 1e-6 &&
      before - start <= maxArcM + 1e-9 &&
      segmentFitsBuildVolume(runtime, start, before, chunkSizeMm);
    const afterOk =
      after < L - 1e-6 &&
      after - start <= maxArcM + 1e-9 &&
      segmentFitsBuildVolume(runtime, start, after, chunkSizeMm);

    if (beforeOk && before < cut) {
      // End this chunk before the support — next chunk owns the connection.
      cut = before;
      continue;
    }
    if (afterOk) {
      // Include the support connection in this chunk; cut past clearance.
      cut = after;
      continue;
    }
    if (before > start + 1e-6) {
      cut = before;
      continue;
    }
    if (after < L - 1e-6) {
      cut = after;
      continue;
    }
    break;
  }

  return Math.min(L, Math.max(start + 1e-6, cut));
}

function sortedUnique(values: number[]): number[] {
  if (values.length === 0) return [];
  const sorted = [...values].sort((a, b) => a - b);
  const out: number[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] - out[out.length - 1] > 1e-6) out.push(sorted[i]);
  }
  return out;
}

/**
 * Absorb leftover slivers (and any other sub-min interval) into a neighbour
 * when the combined arc still fits, otherwise steal arc from a neighbour so
 * both sides stay ≥ minArcM. Never produces an interval that exceeds fitMm.
 */
function mergeTinyIntervals(
  runtime: TrackRuntime,
  boundaries: number[],
  L: number,
  fitMm: number,
  maxArcM: number,
  minArcM: number,
  attaches: number[],
  clearanceM: number,
): void {
  if (boundaries.length <= 2 || L < minArcM * 2) return;

  const fits = (s0: number, s1: number): boolean =>
    s1 - s0 <= maxArcM + 1e-9 && segmentFitsBuildVolume(runtime, s0, s1, fitMm);

  let guard = 0;
  while (guard++ < 64) {
    let changed = false;
    for (let i = 0; i < boundaries.length - 1; i++) {
      const s0 = boundaries[i];
      const s1 = boundaries[i + 1];
      if (s1 - s0 >= minArcM - 1e-9) continue;

      // Prefer merging with the next interval, then the previous.
      if (i + 2 < boundaries.length && fits(s0, boundaries[i + 2])) {
        boundaries.splice(i + 1, 1);
        changed = true;
        break;
      }
      if (i > 0 && fits(boundaries[i - 1], s1)) {
        boundaries.splice(i, 1);
        changed = true;
        break;
      }

      // Steal enough arc from a neighbour that still stays ≥ minArcM.
      const stealFromNext = i + 2 < boundaries.length;
      const stealFromPrev = i > 0;
      if (stealFromNext) {
        const s2 = boundaries[i + 2];
        let cut = s0 + minArcM;
        if (s2 - cut >= minArcM - 1e-9 && fits(s0, cut) && fits(cut, s2)) {
          cut = snapCutAwayFromSupports(runtime, s0, cut, L, fitMm, maxArcM, attaches, clearanceM);
          if (cut > s0 + 1e-6 && s2 - cut >= minArcM - 1e-9 && fits(s0, cut) && fits(cut, s2)) {
            boundaries[i + 1] = cut;
            changed = true;
            break;
          }
        }
      }
      if (stealFromPrev) {
        const prev = boundaries[i - 1];
        let cut = s1 - minArcM;
        if (cut - prev >= minArcM - 1e-9 && fits(prev, cut) && fits(cut, s1)) {
          cut = snapCutAwayFromSupports(runtime, prev, cut, L, fitMm, maxArcM, attaches, clearanceM);
          if (cut - prev >= minArcM - 1e-9 && s1 - cut > 1e-6 && fits(prev, cut) && fits(cut, s1)) {
            boundaries[i] = cut;
            changed = true;
            break;
          }
        }
      }
    }
    if (!changed) break;
  }
}

/** Orthonormal PCA axes of a 3×3 covariance (Jacobi). */
function principalAxes(
  xx: number,
  xy: number,
  xz: number,
  yy: number,
  yz: number,
  zz: number,
): [Vector3, Vector3, Vector3] {
  const A = [
    [xx, xy, xz],
    [xy, yy, yz],
    [xz, yz, zz],
  ];
  const V = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];

  for (let iter = 0; iter < 24; iter++) {
    let p = 0;
    let q = 1;
    let max = Math.abs(A[0][1]);
    const a02 = Math.abs(A[0][2]);
    const a12 = Math.abs(A[1][2]);
    if (a02 > max) {
      p = 0;
      q = 2;
      max = a02;
    }
    if (a12 > max) {
      p = 1;
      q = 2;
      max = a12;
    }
    if (max < 1e-15) break;

    const app = A[p][p];
    const aqq = A[q][q];
    const apq = A[p][q];
    const theta = 0.5 * Math.atan2(2 * apq, aqq - app);
    const c = Math.cos(theta);
    const s = Math.sin(theta);

    for (let i = 0; i < 3; i++) {
      if (i === p || i === q) continue;
      const aip = A[i][p];
      const aiq = A[i][q];
      A[i][p] = A[p][i] = c * aip - s * aiq;
      A[i][q] = A[q][i] = s * aip + c * aiq;
    }
    A[p][p] = c * c * app - 2 * s * c * apq + s * s * aqq;
    A[q][q] = s * s * app + 2 * s * c * apq + c * c * aqq;
    A[p][q] = A[q][p] = 0;

    for (let i = 0; i < 3; i++) {
      const vip = V[i][p];
      const viq = V[i][q];
      V[i][p] = c * vip - s * viq;
      V[i][q] = s * vip + c * viq;
    }
  }

  const ax0 = new Vector3(V[0][0], V[1][0], V[2][0]);
  const ax1 = new Vector3(V[0][1], V[1][1], V[2][1]);
  if (ax0.lengthSq() < 1e-18) ax0.set(1, 0, 0);
  else ax0.normalize();
  ax1.addScaledVector(ax0, -ax1.dot(ax0));
  if (ax1.lengthSq() < 1e-18) {
    const hint = Math.abs(ax0.x) < 0.9 ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0);
    ax1.crossVectors(ax0, hint);
  }
  ax1.normalize();
  const ax2 = new Vector3().crossVectors(ax0, ax1).normalize();
  return [ax0, ax1, ax2];
}
